import { useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import { Trash2, Copy } from 'lucide-react';
import { ChatMessage } from '../../types/chat';

import { StreamingMarkdown } from './StreamingMarkdown';

interface ChatMessagesProps {
  messages: ChatMessage[];
  currentUserId: string;
  onDeleteMessage?: (messageId: string) => void;
  streamingMessageIds?: Set<string>; // IDs сообщений, которые ещё генерируются
}

export function ChatMessages({ messages, currentUserId, onDeleteMessage, streamingMessageIds }: ChatMessagesProps) {
  // Функция копирования текста сообщения
  const handleCopyMessage = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      // Можно добавить уведомление о копировании
    } catch (err) {
      console.error('Ошибка копирования:', err);
    }
  };
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLElement | null>(null);
  const wasAtBottomRef = useRef(true); // По умолчанию считаем что пользователь внизу


  // Проверяем, находится ли пользователь внизу контейнера
  const checkIfAtBottom = () => {
    const container = containerRef.current || messagesEndRef.current?.parentElement;
    if (container) {
      const threshold = 100; // пикселей от низа
      const isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - threshold;
      wasAtBottomRef.current = isAtBottom;
      
      // Кнопка показывается когда пользователь НЕ внизу и есть сообщения

      return isAtBottom;
    }
    return true;
  };



  // Проверяем позицию перед обновлением сообщений
  useEffect(() => {
    if (messagesEndRef.current) {
      const container = messagesEndRef.current.parentElement;
      containerRef.current = container;
      
      if (container) {
        // Проверяем позицию ПЕРЕД обновлением
        checkIfAtBottom();
      }
    }
  });

  // Автоскролл только при первой загрузке (когда нет сообщений и появляются)
  useEffect(() => {
    if (messages.length === 1) {
      // Прокручиваем только при появлении первого сообщения
      const scrollToBottomSafely = () => {
        const container = containerRef.current || messagesEndRef.current?.parentElement;
        if (container && container.scrollHeight > 0) {
          container.scrollTo({
            top: container.scrollHeight,
            behavior: 'auto'
          });
          setTimeout(() => {
            checkIfAtBottom();
          }, 50);
        }
      };

      // Выполняем прокрутку только один раз
      setTimeout(scrollToBottomSafely, 100);
    }
  }, [messages.length]);

  // Обновляем состояние кнопки при изменении содержимого сообщений
  useEffect(() => {
    if (messages.length > 0) {
      // Только проверяем состояние, без принудительной прокрутки
      setTimeout(() => {
        checkIfAtBottom();
      }, 100);
    }
  }, [messages]);

  // Инициализация состояния после монтирования
  useEffect(() => {
    // Только проверяем состояние, без принудительной прокрутки
    setTimeout(() => {
      checkIfAtBottom();
    }, 500);
  }, []);

  // Обновляем состояние кнопки при изменении стриминга, но НЕ принудительно прокручиваем
  useEffect(() => {
    if (messagesEndRef.current && containerRef.current) {
      // Только обновляем состояние кнопки, прокрутку оставляем пользователю
      checkIfAtBottom();
    }
  }, [streamingMessageIds]);

  // Отслеживаем прокрутку пользователя
  useEffect(() => {
    const container = containerRef.current || messagesEndRef.current?.parentElement;
    if (container) {
      const handleScroll = () => {
        checkIfAtBottom();
      };

      // Добавляем обработчик прокрутки
      container.addEventListener('scroll', handleScroll, { passive: true });
      
      // Также проверяем при изменении размера
      const handleResize = () => {
        checkIfAtBottom();
      };
      window.addEventListener('resize', handleResize);

      return () => {
        container.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);



  return (
    <div className="flex-1 overflow-y-auto p-4 relative">
      <div className="space-y-4">
        <AnimatePresence initial={false}>
          {messages.map((message) => {
            const isCurrentUser = message.sender.id === currentUserId;


            return (
              <motion.div
                key={message.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
              >
                <div 
                  className={`flex flex-col ${isCurrentUser ? 'items-end' : 'items-start'} w-full group relative`}
                >
                  
                  {/* Сообщение */}
                  <div
                    className={`px-3 sm:px-4 py-2 w-full break-words ${
                      isCurrentUser
                        ? 'rounded-3xl bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                        : 'rounded-lg text-gray-900 dark:text-gray-100'
                    }`}
                  >
                    {streamingMessageIds?.has(message.id) ? (
                      // Для потоковых сообщений используем улучшенный потоковый markdown парсер
                      <StreamingMarkdown 
                        content={message.content} 
                        isStreaming={true}
                        isFromAI={!isCurrentUser}
                      />
                    ) : (
                      // Для финализированных сообщений используем полный markdown
                      <StreamingMarkdown 
                        content={message.content} 
                        isStreaming={false}
                        isFromAI={!isCurrentUser}
                      />
                    )}
                  </div>
                  
                  {/* Кнопки действий под сообщением */}
                  <div className={`flex items-center gap-1 mt-2 ${isCurrentUser ? 'justify-end' : 'justify-start'}`}>
                    {/* Кнопка копирования */}
                    <button
                      onClick={() => handleCopyMessage(message.content)}
                      className="p-1.5 rounded transition-all duration-200 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      title="Скопировать сообщение"
                    >
                      <Copy className="w-3 h-3 sm:w-4 sm:h-4" />
                    </button>
                    
                    {/* Кнопка удаления */}
                    {onDeleteMessage && (
                      <button
                        onClick={() => onDeleteMessage(message.id)}
                        className="p-1.5 rounded transition-all duration-200 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                        title="Удалить сообщение"
                      >
                        <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
        <div ref={messagesEndRef} />
        
      </div>
      
      
      
    </div>
  );
}
